import { useMemo } from 'react';

import type { NavigationItemConfig, NavigationItemId } from '@config/navigation/navigationItems';
import type { ProductModuleId } from '@config/products/productModules';

import { getVisibleNavigationItems } from '@config/navigation/navigationVisibility';
import { NAV_ITEM_TO_MODULE } from '@config/products/productGating';

import { useCurrentServicesProvider } from './useCurrentServicesProvider';
import { useProductGating } from './useProductGating';

export interface NavigationItemWithAccess {
	/** Unique identifier for the navigation item. */
	id: NavigationItemId;
	/** Static configuration (label, icon, route, etc.) for the navigation item. */
	config: NavigationItemConfig;
	/** Whether the item is locked due to subscription gating. */
	isLocked: boolean;
	/** Product module this item belongs to, if gated. */
	moduleId?: ProductModuleId;
}

/**
 * Unified hook that resolves both visibility and subscription gating for navigation items.
 * Returns only visible items, each annotated with its lock state.
 *
 * Usage:
 *   const { items, isLoading } = useNavigationItems();
 */
export function useNavigationItems(): {
	/** Visible navigation items annotated with lock state. */
	items: NavigationItemWithAccess[];
	/** Whether the underlying provider data is still loading. */
	isLoading: boolean;
} {
	const { data: provider, isLoading } = useCurrentServicesProvider();
	const { lockedNavItems } = useProductGating();

	const items = useMemo(() => {
		const visible = getVisibleNavigationItems(provider);
		return visible.map((item) => {
			const moduleId = NAV_ITEM_TO_MODULE[item.id];
			return {
				id: item.id,
				config: item,
				isLocked: lockedNavItems.has(item.id),
				moduleId: moduleId ?? undefined,
			};
		});
	}, [provider, lockedNavItems]);

	return { items, isLoading };
}
